import { selectSelectedAddons } from 'app/pages/TicketSalePage/slice/selectors';
import config from 'config/config';
import findIndex from 'lodash/findIndex';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  selectEventFromCart,
  selectItemsFromCart,
} from 'store/cartSlice/selectors';
import { TicketAddon as TicketAddonInterface } from '../../../../types/TicketsState';
import {
  buildPriceToShow,
  getFeePrice,
  getTaxPrice,
} from '../../../../utils/tickets';
import { messages } from '../messages';
import { Button } from 'app/components/ui/button';
import { Minus, Plus } from 'lucide-react';

interface TicketAddonProps {
  addon: TicketAddonInterface;
  currency: string;
  ticketId: string;
  ticketIndex: number;
  addAddonToCart: Function;
  displayFee: boolean;
}

const TicketAddon: React.FC<TicketAddonProps> = ({
  addon: {
    id,
    name,
    price,
    orderable_quantity,
    isEventAddon,
    idEventAddon,
    totalQuantity,
  },
  currency,
  ticketId,
  ticketIndex,
  addAddonToCart,
  displayFee,
}) => {
  const { t } = useTranslation();
  const cartEvent = useSelector(selectEventFromCart);
  const { tax_included, tax_rate, fees } = cartEvent;
  const selectedItems = useSelector(selectItemsFromCart);
  const selectedAddons = useSelector(selectSelectedAddons);
  const index = findIndex(selectedItems, {
    addonId: id,
    ticketIndex,
  });
  const addonPrice = tax_included
    ? price + getTaxPrice(price, tax_rate.rate)
    : price;
  const addonFee = getFeePrice(price, fees);
  const initialQuantity = index !== -1 ? selectedItems[index].quantity : 0;
  const [addonQuantity, setAddonQuantity] = useState(initialQuantity);

  let addonAvailableQuantity =
    (orderable_quantity || config.ticketSale.defaultNumberOfAvailableTickets) -
    addonQuantity;

  const checkIfAddonIsLinked = (addonId: string | null) => {
    const allAddons = cartEvent.tickets
      .map(ticket => ticket.ticketaddons || [])
      .flat();

    const addon = allAddons.find(addon => addon.id === addonId);

    return addon?.idEventAddon && addon?.idEventAddon === idEventAddon;
  };

  if (isEventAddon && orderable_quantity !== null) {
    const totalAvailableQuantity =
      totalQuantity -
      selectedAddons
        .filter(item => checkIfAddonIsLinked(item.addonId))
        .reduce((a, c) => a + c.quantity, 0);

    addonAvailableQuantity = Math.min(
      totalAvailableQuantity,
      addonAvailableQuantity,
    );
  }

  const handleClick = value => {
    if (
      (addonAvailableQuantity === 0 && value === 1) ||
      addonQuantity + value < 0
    )
      return;

    addAddonToCart(ticketId, id, value);
    setAddonQuantity(addonQuantity + value);
  };

  const decrementButtonShouldBeDisabled = addonQuantity === 0;

  const incrementButtonShouldBeDisabled = addonAvailableQuantity === 0;

  return (
    <div className="relative flex items-center justify-between p-2 pb-7 sm:pb-2">
      <p className="p1-medium sm:max-w-[180px] mr-[4px]">{name}</p>
      <div className="flex items-center h-fit w-fit absolute bottom-[8px] sm:top-0 sm:right-0 sm:bottom-0 sm:left-0 sm:m-auto">
        <p className="p2-semibold">
          {buildPriceToShow(addonPrice, currency, t(messages.free()))}
        </p>
        {displayFee && addonFee > 0 ? (
          <p className="p4-regular ml-[4px] text-neutral-800">
            {'+'}
            {buildPriceToShow(addonFee, currency)} {t(messages.fee())}
          </p>
        ) : null}
      </div>
      <div className="quantity-buttons-section flex items-center">
        <Button
          type="button"
          size="icon"
          disabled={decrementButtonShouldBeDisabled}
          onClick={() => handleClick(-1)}
        >
          <Minus />
        </Button>
        <p className="p1-medium w-[35px] text-center"> {addonQuantity} </p>
        <Button
          type="button"
          size="icon"
          disabled={incrementButtonShouldBeDisabled}
          onClick={() => handleClick(1)}
        >
          <Plus />
        </Button>
      </div>
    </div>
  );
};

export default TicketAddon;
